import React, { useMemo } from "react";
import Link from "next/link";
import PropTypes from "prop-types";
import { getConstants } from "./langUtils";
const GOOGLE_MAP_API_URL = "https://www.google.com/maps/dir/?api=1";
const ASHRAM = "ashram";
function slugify(name) {
  return name
    .normalize("NFD") // split accents
    .replace(/[\u0300-\u036f]/g, "") // remove accents
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}
function CenterCard({
  meditation,
  onOpenDialog,
  isMobile,
  locale,
  meditationPlacesUrl,
}) {
  const viewDetailsUrl = `${meditationPlacesUrl}${meditation?.slug}/`;
  function floatToTime(number) {
    const sign = number >= 0 ? "" : "-";
    const absNumber = Math.abs(number);
    const hour = Math.floor(absNumber);
    const minute = Math.round((absNumber - hour) * 60)
      .toString()
      .padStart(2, "0");
    return `${sign}${hour}:${minute}`;
  }

  const getCenterTimings = (centerMeditation) => {
    const days = {
      sun: { day: "Sunday", timings: [] },
      mon: { day: "Monday", timings: [] },
      tue: { day: "Tuesday", timings: [] },
      wed: { day: "Wednesday", timings: [] },
      thu: { day: "Thursday", timings: [] },
      fri: { day: "Friday", timings: [] },
      sat: { day: "Saturday", timings: [] },
    };

    const timings = [];

    centerMeditation?.meditationTimings?.forEach(
      ({ active, dayOfTheWeek, meditationTime }) => {
        if (active && days[dayOfTheWeek] && meditationTime != null) {
          const [hourPart, minute] = floatToTime(meditationTime).split(":");
          const hourInt = parseInt(hourPart, 10);
          if (!Number.isNaN(hourInt)) {
            const timePart = hourInt < 12 ? "AM" : "PM";
            const hour = (hourInt % 12 || 12).toString().padStart(2, "0");
            const finalMinute = minute || "00";
            days[dayOfTheWeek].timings.push(
              `${hour}:${finalMinute} ${timePart}`
            );
          }
        }
      }
    );

    Object.values(days).forEach(({ day, timings: dayTimings }) => {
      if (dayTimings.length) {
        timings.push(`${day}: ${dayTimings.join(", ")}`);
      }
    });

    return timings.length ? timings : null;
  };

  const centerTimings = useMemo(
    () => getCenterTimings(meditation),
    [meditation]
  );

  const handleLinkClick = (event) => {
    event.stopPropagation();
  };
  const openGoogleMaps = (latitude, longitude, event) => {
    event.stopPropagation();
    if (latitude && longitude) {
      window.open(
        `${GOOGLE_MAP_API_URL}&destination=${latitude},${longitude}`,
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  return (
    <div
      className={`mb-4 ${isMobile ? "col-md-12 text-left" : "col-md-4"}`}
      key={meditation?.id}
    >
      {isMobile ? (
        <div className="mobile-view">
          <div className="text-start mb-2 mx-2">
            <b>{meditation?.name}</b>
          </div>
          <div className="center-info d-flex my-2 text-start">
            <i className="pi pi-map-marker mx-2" />
            {meditation?.street}, {meditation?.city}, {meditation?.state},{" "}
            {meditation?.country}
          </div>
          <div className="center-info my-2 d-flex text-start">
            <i className="pi pi-clock mx-2" />
            <ul>
              {centerTimings?.map((timing) => <li key={timing}>{timing}</li>) ||
                "-"}
            </ul>
          </div>
          <div className="center-info my-2 text-start">
            <i className="pi pi-envelope mx-2" />
            <span>{meditation?.email ? meditation?.email : "-"}</span>
          </div>
          {(meditation?.mobile?.trim() || meditation?.phone?.trim()) && (
            <div className="center-info mt-2 mb-3 text-start">
              <i className="pi pi-phone mx-2" />
              <span>
                {meditation?.mobile && (
                  <a
                    href={`tel:${meditation?.mobile?.trim()}`}
                    className="phone-link"
                  >
                    {meditation?.mobile?.trim()}
                  </a>
                )}
                {meditation?.mobile && meditation?.phone ? ", " : ""}
                {meditation?.phone && (
                  <a
                    href={`tel:${meditation?.phone?.trim()}`}
                    className="phone-link"
                  >
                    {meditation?.phone?.trim()}
                  </a>
                )}
              </span>
            </div>
          )}
          <div className="mb-2">
            <Link
              href={`${GOOGLE_MAP_API_URL}&destination=${meditation?.latitude},${meditation?.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="submit-btn my-2 mx-1"
              onClick={handleLinkClick}
            >
              {getConstants(locale)?.GETDIRECTIONS}
            </Link>
          </div>
        </div>
      ) : (
        <div className="card w-100">
          <div className="border pt-2">
            <div className="card-body my-1">
              <h4 className="card-title text-center">{meditation?.name}</h4>
            </div>
            <div className="card-footer card-scroll-address">
              {centerTimings && centerTimings.length > 0 && (
                <p className="timing-p">
                  <i className="clocked mx-2" />
                  <div className="timing-container">
                    {Object.entries(
                      centerTimings.reduce((acc, item) => {
                        const [day, time] = item.split(": ");
                        const shortDay = day.slice(0, 3).toUpperCase();

                        acc[shortDay] = acc[shortDay]
                          ? [...acc[shortDay], time]
                          : [time];
                        return acc;
                      }, {})
                    ).map(([day, times]) => (
                      <div key={day} className="timing-group">
                        <strong className="dayofweek">{day}</strong>
                        <span className="meditimings">{times.join(", ")}</span>
                        <hr className="timingline" />
                      </div>
                    ))}
                  </div>
                </p>
              )}

              <div className="footer-sec-phone">
                {(meditation?.mobile?.trim() || meditation?.phone?.trim()) && (
                  <div className="phone-container">
                    <i className="phone" />
                    <div className="phone-content">
                      <a
                        href={`tel:${meditation?.mobile?.trim()}`}
                        className="phone-link"
                      >
                        {meditation?.mobile?.trim()}
                      </a>
                      {meditation?.mobile?.trim() &&
                        meditation?.phone?.trim() &&
                        ", "}
                      {meditation?.phone?.trim() && (
                        <a
                          href={`tel:${meditation?.phone?.trim()}`}
                          className="phone-link"
                        >
                          {meditation?.phone?.trim()}
                        </a>
                      )}
                    </div>
                  </div>
                )}
                {meditation?.email && (
                  <p className="emailwrap">
                    <i className="envelope" />
                    <a
                      href={`mailto:${meditation.email}`}
                      className="email-link"
                    >
                      {meditation.email}
                    </a>
                  </p>
                )}
                <p className="footercheck">
                  <i className="map-marker mx-2" />
                  <p className="cardaddress">
                    {meditation?.street}, {meditation?.street2},{" "}
                    {meditation?.city}, {meditation?.state},{" "}
                    {meditation?.country}
                  </p>
                </p>
              </div>
            </div>
            <div className="footer-sec ">
              <hr className="buttonline" />
              <div className="center-send">
                {" "}
                <button
                  className="btn btn-outline-dark direction"
                  type="button"
                  onClick={(event) =>
                    openGoogleMaps(
                      meditation?.latitude,
                      meditation?.longitude,
                      event
                    )
                  }
                >
                  {getConstants(locale)?.GETDIRECTIONS}
                </button>
                <button
                  className="btn btn-outline-dark sendmsg"
                  type="button"
                  onClick={() => onOpenDialog(meditation?.id, "centers")}
                >
                  {getConstants(locale)?.SEND}
                </button>
              </div>
            </div>
            {meditationPlacesUrl &&
              <div className="view_details_link_section">
                {meditation?.centerType && meditation?.enableCms === true && (
                  <a
                    href={viewDetailsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="view_details_link"
                  >
                    {getConstants(locale)?.DETAILS}
                  </a>
                )}
              </div>
            }

          </div>
        </div>
      )}
    </div>
  );
}

CenterCard.propTypes = {
  meditation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    street: PropTypes.string.isRequired,
    street2: PropTypes.string,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    country: PropTypes.string.isRequired,
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
    email: PropTypes.string,
    phone: PropTypes.string,
    mobile: PropTypes.string,
  }).isRequired,
  onOpenDialog: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
  meditationPlacesUrl: PropTypes.string.isRequired,
};

export default CenterCard;
